import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Tag, Space, Button, Input, Modal, Form, message,
  Typography, InputNumber, Switch, Popconfirm, Select,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,

} from '@ant-design/icons';
import { adminCategoriesAPI } from '../services/api';
import { useLanguage } from '../store/LanguageContext';
import type { Category } from '../types';

const { Title, Text } = Typography;

const AdminCategoryPage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [form] = Form.useForm();

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminCategoriesAPI.getAll();
      setCategories(res.data.categories || []);
    } catch {
      message.error(t('adminPanel.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingCategory(null);
    form.resetFields();
    form.setFieldsValue({ sort_order: 0, is_active: true, level: 0, parent_id: null });
    setModalVisible(true);
  };

  const handleEdit = (cat: Category) => {
    setEditingCategory(cat);
    form.setFieldsValue({
      name: cat.name,
      name_zh: cat.name_zh,
      slug: cat.slug,
      description: cat.description,
      icon: cat.icon,
      sort_order: cat.sort_order,
      is_active: cat.is_active,
      parent_id: cat.parent_id ?? null,
      level: cat.level ?? (cat.parent_id ? 1 : 0),
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingCategory) {
        await adminCategoriesAPI.update(editingCategory.id, values);
        message.success(t('adminPanel.updateSuccess'));
      } else {
        await adminCategoriesAPI.create(values);
        message.success(t('adminPanel.createSuccess'));
      }
      setModalVisible(false);
      fetchCategories();
    } catch {
      message.error(t('adminPanel.updateFailed'));
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await adminCategoriesAPI.delete(id);
      message.success(t('adminPanel.deleteSuccess'));
      fetchCategories();
    } catch {
      message.error(t('adminPanel.deleteFailed'));
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
    },
    {
      title: t('adminPanel.colIcon'),
      dataIndex: 'icon',
      width: 60,
      render: (icon: string) => <span style={{ fontSize: 20 }}>{icon || '📁'}</span>,
    },
    {
      title: t('adminPanel.colCatName'),
      dataIndex: 'name',
      render: (name: string, record: Category) => (
        <div>
          <Text strong>{name}</Text>
          {record.name_zh && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{record.name_zh}</Text>}
        </div>
      ),
    },
    {
      title: 'Slug',
      dataIndex: 'slug',
      render: (slug: string) => <Tag>{slug}</Tag>,
    },
    {
      title: '层级',
      dataIndex: 'level',
      width: 90,
      render: (level: number | undefined, record: Category) =>
        (level === 1 || record.parent_id) ? <Tag color="blue">叶子</Tag> : <Tag>父级</Tag>,
    },
    {
      title: t('adminPanel.colToolCount'),
      dataIndex: 'tool_count',
      render: (v: number) => v ?? 0,
      sorter: (a: Category, b: Category) => (a.tool_count || 0) - (b.tool_count || 0),
    },
    {
      title: t('adminPanel.colSortOrder'),
      dataIndex: 'sort_order',
    },
    {
      title: t('adminPanel.colStatus'),
      dataIndex: 'is_active',
      render: (active: boolean) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? t('adminPanel.active') : t('adminPanel.disabled')}
        </Tag>
      ),
    },
    {
      title: t('admin.colActions'),
      key: 'actions',
      width: 150,
      render: (_: any, record: Category) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('adminPanel.confirmDelete')}
            onConfirm={() => handleDelete(record.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={3} style={{ margin: 0 }}>{t('adminPanel.categoryManagement')}</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchCategories}>{t('admin.refresh')}</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('adminPanel.createCategory')}
          </Button>
        </Space>
      </div>

      <Card style={{ borderRadius: 12 }}>
        <Table
          loading={loading}
          dataSource={categories}
          columns={columns}
          rowKey="id"
          pagination={false}
        />
      </Card>

      <Modal
        title={editingCategory ? t('adminPanel.editCategory') : t('adminPanel.createCategory')}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText={t('admin.save')}
        cancelText={t('admin.cancel')}
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t('adminPanel.colCatName') + ' (EN)'} name="name" rules={[{ required: true }]}>
            <Input placeholder="e.g. AI Writing" />
          </Form.Item>
          <Form.Item label={t('adminPanel.colCatName') + ' (中文)'} name="name_zh">
            <Input placeholder="e.g. AI写作" />
          </Form.Item>
          <Form.Item label="Slug" name="slug" rules={[{ required: true }]}>
            <Input placeholder="e.g. ai-writing" />
          </Form.Item>
          <Form.Item label="父级分类" name="parent_id">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="不选则为父级"
              options={categories
                .filter((c) => !c.parent_id && c.id !== editingCategory?.id)
                .map((c) => ({
                  value: c.id,
                  label: `${c.name_zh || c.name} (${c.slug})`,
                }))}
              onChange={(v) => form.setFieldsValue({ level: v ? 1 : 0 })}
            />
          </Form.Item>
          <Form.Item label="层级 level" name="level">
            <InputNumber min={0} max={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('adminPanel.colDescription')} name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item label={t('adminPanel.colIcon')} name="icon">
            <Input placeholder="e.g. ✍️" />
          </Form.Item>
          <Form.Item label={t('adminPanel.colSortOrder')} name="sort_order">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('adminPanel.colStatus')} name="is_active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminCategoryPage;
